import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { articleContents, articleContentsB } from '@/mocks/articleContents';

const allContents = { ...articleContents, ...articleContentsB };

export default function ArticlePage() {
  const { t } = useTranslation();
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const content = slug ? allContents[slug] : undefined;

  if (!content) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <main className="pt-24 md:pt-28">
          <div className="w-full px-4 md:px-8 lg:px-12 max-w-3xl mx-auto pb-20 text-center">
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-warm-900 mb-4">
              {t('notFound.title')}
            </h1>
            <p className="text-warm-500 mb-6">{t('notFound.description')}</p>
            <Link to="/" className="inline-flex items-center gap-2 px-6 py-3 bg-brand-600 text-white font-medium rounded-full hover:bg-brand-700 transition-colors whitespace-nowrap">
              {t('notFound.cta')}
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="pt-24 md:pt-28">
        <article className="w-full px-4 md:px-8 lg:px-12 max-w-3xl mx-auto pb-20">
          <nav className="flex items-center gap-2 text-sm mb-8 flex-wrap">
            <Link to="/" className="text-warm-500 hover:text-brand-600 transition-colors">{t('breadcrumb.home')}</Link>
            <i className="ri-arrow-right-s-line text-warm-400 text-xs" />
            <Link to="/pt/articles" className="text-warm-500 hover:text-brand-600 transition-colors">{t('nav.articles')}</Link>
            <i className="ri-arrow-right-s-line text-warm-400 text-xs" />
            <span className="text-warm-700">{content.category}</span>
          </nav>

          <header className="mb-10">
            <span className="inline-block px-3 py-1 rounded-full bg-brand-50 text-brand-700 text-xs font-medium mb-4">
              {content.category}
            </span>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-warm-900 leading-tight mb-4">
              {content.title}
            </h1>
            <div className="flex items-center gap-4 text-sm text-warm-400">
              <span>{t('general.lastUpdated')}: {content.lastUpdated}</span>
              <span>{content.readTime} {t('general.readTime')}</span>
            </div>
          </header>

          <div className="prose prose-warm max-w-none">
            <div className="bg-brand-50 rounded-xl border border-brand-100 p-5 md:p-6 mb-8">
              <p className="text-sm md:text-base text-warm-700 leading-relaxed m-0">
                <strong>{t('general.quickAnswer', { defaultValue: 'Resposta rapida:' })}</strong>{' '}
                {content.quickAnswer}
              </p>
            </div>

            {content.sections.map((section, idx) => (
              <div key={idx}>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-warm-900 mt-10 mb-4">
                  {section.heading}
                </h2>
                {section.paragraphs.map((para, pIdx) => (
                  <p key={pIdx} className="text-sm md:text-base text-warm-600 leading-relaxed mb-4">
                    {para}
                  </p>
                ))}
              </div>
            ))}

            <h2 className="font-heading text-2xl md:text-3xl font-bold text-warm-900 mt-10 mb-4">
              {t('general.checklist', { defaultValue: 'Checklist' })}
            </h2>
            <ul className="space-y-3 mb-6">
              {content.checklist.map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-emerald-50 border border-emerald-200 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-check-line text-emerald-600 text-xs" />
                  </div>
                  <span className="text-sm md:text-base text-warm-600">{item}</span>
                </li>
              ))}
            </ul>

            <h2 className="font-heading text-2xl md:text-3xl font-bold text-warm-900 mt-10 mb-4">
              {t('faq.h2')}
            </h2>
            <div className="space-y-4 mb-8">
              {content.faqs.map((faq, i) => (
                <div key={i} className="bg-warm-50 rounded-xl border border-warm-200 p-5">
                  <h3 className="font-heading text-base md:text-lg font-semibold text-warm-900 mb-2">
                    {faq.q}
                  </h3>
                  <p className="text-sm md:text-base text-warm-600 leading-relaxed">{faq.a}</p>
                </div>
              ))}
            </div>

            <h2 className="font-heading text-2xl md:text-3xl font-bold text-warm-900 mt-10 mb-4">
              {t('general.relatedArticles')}
            </h2>
            <div className="grid gap-3 mb-8">
              {content.relatedLinks.map((link) => (
                link.external ? (
                  <a
                    key={link.href}
                    href={link.href}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-2 px-4 py-3 bg-warm-50 rounded-lg border border-warm-200 hover:border-brand-200 text-sm text-warm-700 hover:text-brand-600 transition-colors"
                  >
                    <i className="ri-arrow-right-line text-brand-600 text-xs" />
                    {link.label}
                  </a>
                ) : (
                  <Link
                    key={link.href}
                    to={link.href}
                    className="flex items-center gap-2 px-4 py-3 bg-warm-50 rounded-lg border border-warm-200 hover:border-brand-200 text-sm text-warm-700 hover:text-brand-600 transition-colors"
                  >
                    <i className="ri-arrow-right-line text-brand-600 text-xs" />
                    {link.label}
                  </Link>
                )
              ))}
            </div>

            <div className="bg-warm-900 rounded-xl px-6 py-8 md:px-8 md:py-10 text-center mt-12">
              <h3 className="font-heading text-xl md:text-2xl font-bold text-white mb-3">
                {t('ctaBanner.h2')}
              </h3>
              <p className="text-sm text-warm-300 mb-6">
                {t('ctaBanner.description')}
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="px-6 py-3 bg-brand-600 text-white font-medium rounded-full hover:bg-brand-500 transition-colors whitespace-nowrap">
                  {t('hero.ctaMainSite')}
                </a>
                <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="px-6 py-3 bg-white text-warm-900 font-medium rounded-full hover:bg-warm-100 transition-colors whitespace-nowrap">
                  {t('hero.ctaBuyNow')}
                </a>
              </div>
            </div>
          </div>

          <div className="mt-12 pt-6 border-t border-warm-200">
            <p className="text-xs text-warm-400">
              {t('general.disclaimer')}
            </p>
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
}